import { useCallback, useMemo } from "react";
import { useApolloClient } from "@apollo/client";
import { isEqual, noop } from "lodash";

import store, { RootState, useAppDispatch, useAppSelector } from "../../../Store";

import Graph_QL, { DynamicDataObk, GqlHook_Returns, StateDataObject } from "..";
import { gqlActions } from "../gqlData_Reducer";
import { filterByConnection } from "../filterByConnection";

import { BasicQueryInfoInput } from "./GQLCalls/getFeedComponentsCall";
import { FeedCurveItem } from "./FeedDataSlices/oldFeedCurveSlice";
import { CREATE_FEED_CURVE, CreateFeedCurveResponse } from "./GQLCalls/createFeedCurveCall";
import { GET_FEED_CURVES, FeedCurvesResponse, FeedCurve } from "./GQLCalls/getFeedCurvesCall";
import { MAINTAIN_FEED_CURVE, MaintainFeedCurveCreates, MaintainFeedCurveDelete, MaintainFeedCurveResponse, MaintainFeedCurveUpdates, MaintainFeedCurveVariables } from "./GQLCalls/maintainFeedCurveCall";
import { getFarmSettingsState } from "../UserCalls/dataCalls";
import { DateTime } from "luxon";
import { SKIP_FEED, SkipFeedMutationResponse } from "./GQLCalls/skipFeedCalls";
import { COPY_FEED_CURVE, CopyFeedCurveResponse } from "./GQLCalls/copyFeedCurveCall";
import {  upsertFeedCurves } from "./FeedDataSlices/feedCurvesSlice";
import { DELETE_FEED_CURVE, DeleteFeedCurve } from "./GQLCalls/deleteFeedCurveCall";
import { clearFeedCurveMilestoneOptions, clearFeedCurveOptions } from "../../../Store/OptionsHandlers/optionsCombinedReducer";
import { selectAnimalsByFeedCurve } from "../AnimalDataCalls/AnimalDataSlices/animalsSlice";
import { dispatchFetchConsumptionsByAnimalIds } from "../ConsumptionCalls/consumptionSlice";

interface maintainFeedCurveElements {
  createFeedCurve: (name: string, specific?: Graph_QL.GQLCons) => Promise<{ success: boolean, feedcurve_id: number }>, 
  deleteFeedCurve: (deletedId: number, specific?: Graph_QL.GQLCons) => Promise<{ success: boolean; feedcurve_id: number; message: string }>,
  copyFeedCurve: (name: string, oldFeedCurveId: number, specific?: Graph_QL.GQLCons) => Promise<{ success: boolean, feedcurve_id: number }>, 
  feedSkip: (locationid: number, amount: number, specific?: Graph_QL.GQLCons) => Promise<boolean>;
}

export interface FeedCurveDirectives {
  ifFeedCurveBaseInfoOnly?: boolean,
  ifFeedCurveInfo?: boolean,
  ifSpreading?: boolean,
}

const getFeedCurveState = (state: RootState) => {return state.graphQlData.domain.feedCurves};


export const useFeedCurves = (connection: Graph_QL.Connection): GqlHook_Returns<FeedCurveItem, BasicQueryInfoInput & FeedCurveDirectives> & maintainFeedCurveElements => {
  const dispatch = useAppDispatch();
  const client = useApolloClient();
  const farmSettings = useAppSelector(getFarmSettingsState);

  const timezoneOffset = useMemo(() => {
    const timezoneOffset = DateTime.now().setZone(farmSettings?.time_zone_label);
    return timezoneOffset.offset - (timezoneOffset.isInDST ? 60 : 0)
  },[farmSettings?.time_zone_label])
  
  const feedCurvesState = useAppSelector(getFeedCurveState);
  const {fullState} = useMemo(() => {
    const state = filterByConnection(feedCurvesState, Graph_QL.ToArr(connection)) as Record<string, FeedCurveItem>
    return Object.entries(state).reduce((accumulator, [key, data]) => {
      if (data.data) {
        const value = {...data.data, domain:key}; 
        accumulator.data = accumulator.data.concat(value)
      }
      return accumulator
    },{fullState: state, data: []} as StateDataObject<FeedCurveItem>)
  },[connection, feedCurvesState]);

  const getFeedCurves = useCallback(async (input: BasicQueryInfoInput & FeedCurveDirectives, specific?: Graph_QL.GQLCons,  ) => {
    const {ifFeedCurveBaseInfoOnly, ifFeedCurveInfo, ifSpreading, ...remainingInput} = input
    const usedConnection = specific?.connection ? specific.connection : connection.connection 
    try {
      client.query<FeedCurvesResponse>({query: GET_FEED_CURVES, variables: {...usedConnection, input: remainingInput, ifFeedCurveBaseInfoOnly: ifFeedCurveBaseInfoOnly ?? false, ifFeedCurveInfo: ifFeedCurveInfo ?? true, ifSpreading: ifSpreading ?? false}, fetchPolicy: input.ids && input.ids?.length > 0 ? "network-only" : undefined})
      .then((e) => {
        if (e && e.data) {
          dispatch({type: ifFeedCurveBaseInfoOnly ? String(gqlActions.domain.feedCurves.update) : String(gqlActions.domain.feedCurves.maintained), payload: {con: usedConnection, data: e.data.feedCurve.info.map((feedCurve) => {return {...feedCurve, times: feedCurve.times?.map((times) => {return {...times, time: times.time + (timezoneOffset)}})}})}});
          dispatch(upsertFeedCurves(e.data.feedCurve.info.map((feedCurve) => {return {...feedCurve, times: feedCurve.times?.map((times) => {return {...times, time: times.time + (timezoneOffset)}})}})))
        }
      }).catch((error) => {console.log(error);})
    } catch (error) {
      console.log(error);
      // TODO CTO: clean up when everything is stable
    }
  },[client, connection.connection, dispatch, timezoneOffset])
 
  //   useEffect(() => {
  //   if (!(fullState[Graph_QL.ObjToString(connection.connection)]?.data?.length < 50)) {
  //     getFeedCurves({fetchAmount: 50, ifBaseInfoOnly: true})
  //   }
  // },[connection.connection, fullState, getFeedCurves])
  
  const fetchMore = async (cursor: number|number[], specific?: Graph_QL.GQLCons) => {
    
    }

  const maintainFeedCurve =  useCallback(async (changed?: Partial<DynamicDataObk<MaintainFeedCurveUpdates>>, deleted?: Partial<DynamicDataObk<MaintainFeedCurveDelete>>, created?: Partial<DynamicDataObk<MaintainFeedCurveCreates>>, feedCurve?: {originalFeedCurve: FeedCurve, optimisticFeedCurve: FeedCurve}) => {
    if (feedCurve === undefined) {
      const returnValue: [boolean, string] = [false, "Missing feed curve"];
      return returnValue;
    }
    const maintainFeedCurve = {creates: {...created?.data, times: created?.data?.times}, deletes: deleted?.data, updates: {...changed?.data, times: changed?.data?.times?.map((time) => {return {...time, baseInfo: {time: time.baseInfo.time ? time.baseInfo?.time  : undefined}}})}, feedCurveId: feedCurve.originalFeedCurve.feedCurveId}
    try {
      dispatch({type: String(gqlActions.domain.feedCurves.maintained), payload: {con: connection.connection, data: [feedCurve.optimisticFeedCurve]}})
      return client.mutate<MaintainFeedCurveResponse, MaintainFeedCurveVariables>({mutation: MAINTAIN_FEED_CURVE, variables: {...connection.connection, maintainFeedCurve}})
        .then((response) => {
          if (response && response.data) {
            if (response.data.feedCurve.maintain < 0) {
              // TODO consider alert that something went wrong when saving
              dispatch({type: String(gqlActions.domain.feedCurves.maintained), payload: {con: connection.connection, data: [feedCurve.originalFeedCurve]}})
              const returnValue: [boolean, string] = [false, `Partial update of feed curve failed`]
              dispatch(clearFeedCurveMilestoneOptions());
              dispatch(clearFeedCurveOptions());
              if (changed) {
                const animalWithFeedCurve = selectAnimalsByFeedCurve(store.getState(), [feedCurve.originalFeedCurve.feedCurveId])
                dispatch(dispatchFetchConsumptionsByAnimalIds(animalWithFeedCurve.map((animal) => animal.animal_id.toString()), true))
              }
              return returnValue;
            }
            getFeedCurves({ids: [response.data.feedCurve.maintain], ifSpreading: true})
            const returnValue: [boolean, string] = [true, "Success"];
            return returnValue;
          }
        }).catch((error) => {
          console.log(error);
          const returnValue: [boolean, string] = [false, `Partial update of feed curve failed with error: ${error}`];
          return returnValue;
        })
    } catch (error) {
      console.log(error)
      const returnValue: [boolean, string] = [false, `Partial update of feed curve failed with error: ${error}`];
      return returnValue; 
    }
  },[client, connection.connection, dispatch, getFeedCurves])

  const createFeedCurve = useCallback(async (name: String, specific?: Graph_QL.GQLCons) => {
    const usedConnection = specific ? specific.connection : connection.connection 
    try {
      const response = await client.mutate<CreateFeedCurveResponse>({mutation: CREATE_FEED_CURVE, variables: {...usedConnection, name}})
      if (response) {
        const createdFeedCurve = response.data?.feedCurve.create;
        if (createdFeedCurve && createdFeedCurve.success && createdFeedCurve.updatedValues.length > 0) {
          const createdFeedCurveInfo = createdFeedCurve.updatedValues[0];
          dispatch({type: String(gqlActions.domain.feedCurves.maintained), payload: {con: usedConnection, data: [{name, feedCurveId: createdFeedCurveInfo.feedcurve_id, milestones: []}]}})
          getFeedCurves({ids: [createdFeedCurveInfo.feedcurve_id], ifSpreading: true});
          dispatch(clearFeedCurveMilestoneOptions());
          dispatch(clearFeedCurveOptions());
          return {success: true, feedcurve_id: createdFeedCurveInfo.feedcurve_id}
        }
      }
  
    return {success: false, feedcurve_id: -1};
    } catch (error) {
      return {success: false, feedcurve_id: -1};
    }
  }, [client, connection.connection, dispatch, getFeedCurves])

  // #region | Delete FeedCurve
  const deleteFeedCurve = useCallback(
    async (deletedId: number, specific?: Graph_QL.GQLCons) => {
      const usedConnection = specific ? specific.connection : connection.connection;

      try {
        const response = await client.mutate<DeleteFeedCurve>({ mutation: DELETE_FEED_CURVE, variables: { ...usedConnection, deleteId: deletedId }, fetchPolicy: "no-cache" });
        const result = response.data?.feedCurve.delete;
        if (result?.success) { dispatch({ type: String(gqlActions.domain.feedCurves.deleted), payload: { con: connection.connection, data: [{feedCurveId: deletedId}] } });
          dispatch(clearFeedCurveMilestoneOptions());
          dispatch(clearFeedCurveOptions());
          return { success: true, feedcurve_id: deletedId, message: result.message };
        }
        return { success: false, feedcurve_id: -1, message: result?.message ?? "Unknown error" };
      } catch (err) {
        if (typeof err === "string") {
          return { success: false, feedcurve_id: -1, message: err };
        }
        if (err && typeof err === "object" && "message" in err && typeof err.message === "string") {
          return { success: false, feedcurve_id: -1, message: err.message };
        }
        return {success: false, feedcurve_id: -1, message: "Network or server error"}
      }
    },
    [client, connection.connection, dispatch]
  );
  // #endregion
  
  const copyFeedCurve = useCallback(async (name: string, oldFeedCurveId: number, specific?: Graph_QL.GQLCons) => {
    const usedConnection = specific ? specific.connection : connection.connection 
    try {
      const response = await client.mutate<CopyFeedCurveResponse>({mutation: COPY_FEED_CURVE, variables: {...usedConnection, name, oldFeedCurveId}})
      if (response) {
        const createdFeedCurve = response.data?.feedCurve.copy;
        if (createdFeedCurve && createdFeedCurve.success && createdFeedCurve.updatedValues.length > 0) {
          const createdFeedCurveInfo = createdFeedCurve.updatedValues[0];
          dispatch(clearFeedCurveMilestoneOptions());
          dispatch(clearFeedCurveOptions());
          dispatch({type: String(gqlActions.domain.feedCurves.maintained), payload: {con: usedConnection, data: [{name, feedCurveId: createdFeedCurveInfo.feedcurve_id, milestones: []}]}})
          getFeedCurves({ids: [createdFeedCurveInfo.feedcurve_id], ifSpreading: true});
          return {success: true, feedcurve_id: createdFeedCurveInfo.feedcurve_id}
        }
      }
  
    return {success: false, feedcurve_id: -1};
    } catch (error) {
      return {success: false, feedcurve_id: -1};
    }
  },[client, connection.connection, dispatch, getFeedCurves])
  
    const feedSkip = useCallback(async (locationId: number, amount: number, specific?: Graph_QL.GQLCons) => {
      const usedConnection = specific ? specific.connection : connection.connection
      try {
        return client.mutate<SkipFeedMutationResponse>({mutation: SKIP_FEED, variables: {...usedConnection, locationid: locationId, amount}})
        .then((e) => {
          const feedSkip = e.data?.location.skipFeed;
          if (feedSkip) {
            return feedSkip.success ?? false;
          }
          return false;
        }).catch((error) => {return false})
      } catch (error) {console.log(error); return false}
  },[client, connection.connection])
  
  return {call: getFeedCurves, fetchMore, deleteEntries: noop, data: fullState, maintainEntries: maintainFeedCurve, createFeedCurve, deleteFeedCurve, copyFeedCurve, feedSkip}
} 

